import type { CompanyInput } from '../../repos/companiesRepo';
import type { Company, PersonRecord, Site } from '../../schemas';
import { slugifyItalian } from '../../idGenerator';
import { newId } from '../../../utils/ids';

import { addDays, addMonths, toIsoDate, toIsoDateTime } from './dates';
import { pick, pickWeighted, randomInt, type Rng } from './rng';
import { FIRST_NAMES, LAST_NAMES, PERSON_ROLES } from './people';
import { generateSite } from './seedSites';
import type { GeneratedCompany } from './types';

const SITE_COUNT_DISTRIBUTION = [
  { value: 1, weight: 0.7 },
  { value: 2, weight: 0.25 },
  { value: 3, weight: 0.05 },
] as const;

const COMPANY_PREFIXES = [
  'Alpi',
  'Nordic',
  'Lombarda',
  'Mediterranea',
  'Steel',
  'Bio',
  'Green',
  'Euro',
  'Ventus',
  'Polar',
  'Italia',
  'Fresco',
];

const COMPANY_SUFFIXES = [
  'Cooling',
  'Foods',
  'Logistics',
  'Refrigeration',
  'Industria',
  'Distribuzione',
  'Energy',
  'Ambient',
  'Storage',
  'Dynamics',
];

const COMPANY_FORMS = ['S.p.A.', 'S.r.l.'];

const CITY_OPTIONS = [
  { city: 'Milano', province: 'MI', region: 'Lombardia' },
  { city: 'Bergamo', province: 'BG', region: 'Lombardia' },
  { city: 'Parma', province: 'PR', region: 'Emilia-Romagna' },
  { city: 'Verona', province: 'VR', region: 'Veneto' },
  { city: 'Torino', province: 'TO', region: 'Piemonte' },
  { city: 'Bari', province: 'BA', region: 'Puglia' },
  { city: 'Roma', province: 'RM', region: 'Lazio' },
  { city: 'Napoli', province: 'NA', region: 'Campania' },
  { city: 'Firenze', province: 'FI', region: 'Toscana' },
  { city: 'Piacenza', province: 'PC', region: 'Emilia-Romagna' },
  { city: 'Padova', province: 'PD', region: 'Veneto' },
  { city: 'Ancona', province: 'AN', region: 'Marche' },
];

function generatePersonnel(companyDomain: string, rng: Rng): PersonRecord[] {
  const total = randomInt(rng, 4, 6);
  const timestamp = new Date().toISOString();

  return Array.from({ length: total }, (_unused, index) => {
    const firstName = pick(rng, FIRST_NAMES);
    const lastName = pick(rng, LAST_NAMES);
    const licensed = rng() > 0.25;
    const licenseExpiry = licensed
      ? toIsoDate(addMonths(new Date(), randomInt(rng, 6, 18)))
      : undefined;
    const licenseLastUpdate = licensed
      ? toIsoDate(addMonths(new Date(), -randomInt(rng, 6, 12)))
      : undefined;

    return {
      id: newId(),
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${companyDomain}`,
      phone: `+39 ${randomInt(rng, 320, 389)} ${randomInt(rng, 1000000, 9999999)}`,
      role: pick(rng, PERSON_ROLES),
      scope: 'company',
      reportRecipient: index < 2,
      alarmContact: index === 0,
      fireTraining: rng() > 0.3,
      firstAidTraining: rng() > 0.35,
      licensed,
      licenseExpiry,
      licenseLastUpdate,
      reperibile: index === 0 || rng() > 0.7,
      createdAt: timestamp,
      updatedAt: timestamp,
    } satisfies PersonRecord;
  });
}

function generateSiteScopedPersonnel(site: Site, companyDomain: string, rng: Rng): PersonRecord[] {
  const count = randomInt(rng, 1, 3);
  const timestamp = new Date().toISOString();
  const sequenceTag =
    typeof site.sequenceNumber === 'number' && site.sequenceNumber > 0
      ? `s${String(site.sequenceNumber).padStart(2, '0')}`
      : site.name;
  const siteSlug = slugifyItalian(`${sequenceTag}-${site.name}`);

  return Array.from({ length: count }, (_unused, index) => {
    const firstName = pick(rng, FIRST_NAMES);
    const lastName = pick(rng, LAST_NAMES);
    const licensed = rng() > 0.4;
    const licenseExpiry = licensed
      ? toIsoDate(addMonths(new Date(), randomInt(rng, 6, 18)))
      : undefined;
    const licenseLastUpdate = licensed
      ? toIsoDate(addMonths(new Date(), -randomInt(rng, 6, 12)))
      : undefined;

    return {
      id: newId(),
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}+${siteSlug}@${companyDomain}`,
      phone: `+39 ${randomInt(rng, 320, 389)} ${randomInt(rng, 1000000, 9999999)}`,
      role: pick(rng, PERSON_ROLES),
      scope: 'site',
      siteId: site.id,
      reportRecipient: index === 0 ? true : rng() > 0.4,
      alarmContact: index === 0 ? true : rng() > 0.6,
      fireTraining: rng() > 0.45,
      firstAidTraining: rng() > 0.5,
      licensed,
      licenseExpiry,
      licenseLastUpdate,
      reperibile: index === 0 || rng() > 0.6,
      createdAt: timestamp,
      updatedAt: timestamp,
    } satisfies PersonRecord;
  });
}

export function clonePersonnel(personnel: PersonRecord[]): PersonRecord[] {
  return personnel.map((person) => ({ ...person }));
}

export function evolvePersonnel(
  personnel: PersonRecord[],
  visitDate: Date,
  rng: Rng
): PersonRecord[] {
  if (!personnel.length) return [];
  const evolved = clonePersonnel(personnel);

  // Ensure alarm reference persists as a single contact
  evolved.forEach((person, index) => {
    person.alarmContact = index === 0;
  });
  evolved[0]!.reportRecipient = true;

  if (rng() < 0.45) {
    const licensed = evolved.filter((person) => person.licensed);
    if (licensed.length) {
      const target = licensed[randomInt(rng, 0, licensed.length - 1)];
      target.licenseLastUpdate = toIsoDate(addMonths(visitDate, -randomInt(rng, 0, 6)));
      target.licenseExpiry = toIsoDate(addMonths(visitDate, randomInt(rng, 12, 24)));
    }
  }

  if (rng() < 0.3 && evolved.length > 2) {
    const candidateIndices = evolved.map((_p, idx) => idx).filter((idx) => idx !== 0);
    const handoverIndex = candidateIndices[randomInt(rng, 0, candidateIndices.length - 1)];
    evolved.forEach((person, idx) => {
      person.reportRecipient = idx === 0 || idx === handoverIndex;
    });
  }

  if (rng() < 0.4) {
    const target = evolved[randomInt(rng, 0, evolved.length - 1)];
    target.fireTraining = rng() > 0.2;
    target.firstAidTraining = rng() > 0.25;
  }

  const updatedAt = toIsoDateTime(addDays(visitDate, randomInt(rng, 1, 5)));
  evolved.forEach((person) => {
    person.updatedAt = updatedAt;
  });

  return evolved;
}

// ---------------------------------------------------------------------------
// Company generation
// ---------------------------------------------------------------------------

export function generateCompany(index: number, rng: Rng, historyLength: number): GeneratedCompany {
  const companyId = newId();
  const prefix = pick(rng, COMPANY_PREFIXES);
  const suffix = pick(rng, COMPANY_SUFFIXES);
  const legalForm = pick(rng, COMPANY_FORMS);
  const name = `${prefix} ${suffix} ${legalForm}`;
  const domain = `${slugifyItalian(`${prefix}-${suffix}`)}.it`;
  const timestamp = new Date().toISOString();
  const personnel = generatePersonnel(domain, rng);
  const vat = `IT${randomInt(rng, 10_000_000_000, 99_999_999_999)}`;

  const cityInfo = pick(rng, CITY_OPTIONS);
  const sedeLegaleAddress = `Via ${pick(rng, ['Industriale', 'Galvani', 'Lavoro', "Dell'Artigianato"])}, ${randomInt(rng, 1, 99)}`;
  const sedeLegaleCap = String(randomInt(rng, 10000, 98100));
  const sedeLegaleCity = cityInfo.city;
  const sedeLegaleProvince = cityInfo.province;
  const sedeLegale = `${sedeLegaleAddress}, ${sedeLegaleCap} ${sedeLegaleCity} (${sedeLegaleProvince})`;

  const company: Company = {
    id: companyId,
    name,
    code: `${prefix.slice(0, 2)}${suffix.slice(0, 2)}${String(index + 1).padStart(2, '0')}`.toUpperCase(),
    vat,
    sedeLegale,
    sedeLegaleAddress,
    sedeLegaleCap,
    sedeLegaleCity,
    sedeLegaleProvince,
    notes: undefined,
    createdAt: timestamp,
    updatedAt: timestamp,
    personnel,
  };

  const companyInput: CompanyInput = {
    id: companyId,
    name,
    code: company.code,
    vat,
    sedeLegale: company.sedeLegale,
    sedeLegaleAddress: company.sedeLegaleAddress,
    sedeLegaleCap: company.sedeLegaleCap,
    sedeLegaleCity: company.sedeLegaleCity,
    sedeLegaleProvince: company.sedeLegaleProvince,
    notes: company.notes,
    logoUrl: company.logoUrl,
    createdAt: company.createdAt,
    updatedAt: company.updatedAt,
  };

  const siteCount = pickWeighted(rng, SITE_COUNT_DISTRIBUTION);
  const sites = Array.from({ length: siteCount }, (_unused, siteIndex) =>
    generateSite(companyId, siteIndex, rng, historyLength)
  );

  const siteScopedPersonnel = sites.flatMap((entry) =>
    generateSiteScopedPersonnel(entry.site, domain, rng)
  );

  if (siteScopedPersonnel.length > 0) {
    company.personnel = [...company.personnel, ...siteScopedPersonnel];
  }

  return {
    index,
    historyLength,
    company,
    input: companyInput,
    sites,
  };
}
